import { GripVertical, Hand } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Header, Popup, Table, Icon, Segment } from 'semantic-ui-react';

import { useConfidencesMap, useSetHighlightedMappingsMap } from '../../../../stores/mappingStore';
import { HighlightedMappingMapType, ImportTableDescriptorType, RowIdType, RowMappingType } from '../../../../types';
import MappingsAlternativesIndication from '../mappingsAlternativesIndication';
import './styles.css';
import {
    ROW_MAPPING_TARGET,
    ROW_MAPPING_TYPE,
    TEMPORARY_DESCRIPTOR_ID_PREFIX,
} from '../../../../constants/generalConstants';

type ImportDescriptorPropsType = {
    mapping: RowMappingType;
    importDescriptor: ImportTableDescriptorType;
    handleRemoveImportDescriptor: (rowId: RowIdType) => void;
};

function ImportDescriptor({ mapping, importDescriptor, handleRemoveImportDescriptor }: ImportDescriptorPropsType) {
    const confidencesMap = useConfidencesMap();
    const setHighlightedMappingsMap = useSetHighlightedMappingsMap();
    const newDescriptor = mapping.descriptorId.startsWith(TEMPORARY_DESCRIPTOR_ID_PREFIX);

    const hasAlternativeMappings = useMemo(
        () =>
            !!mapping.alternatives.filter(
                (alm) => alm.target !== ROW_MAPPING_TARGET.SELF && alm.type !== ROW_MAPPING_TYPE.INITIAL
            ).length,
        [mapping.alternatives]
    );

    const highlightAlternatives = useCallback(() => {
        const alternativesMap = mapping.alternatives.reduce((acc, curr) => {
            acc[curr.descriptorId] = confidencesMap[mapping.rowId]?.[curr.descriptorId] ?? 0;
            return acc;
        }, {} as HighlightedMappingMapType);
        alternativesMap[mapping.rowId] = mapping.confidence;
        setHighlightedMappingsMap(alternativesMap);
    }, [mapping.alternatives, confidencesMap, setHighlightedMappingsMap]);
    return (
        <Popup
            pinned
            on={'click'}
            content={
                <div>
                    <Header as="h4">Mapping</Header>
                    <Segment>
                        Row Id: {mapping.rowId} <br />
                        Descriptor Id: {mapping.descriptorId} <br />
                        Confidence:{mapping.confidence.toFixed(2)}%<Header as="h4">Matches</Header>
                        <Table className="descriptorMatches">
                            {mapping.matchChecks.map((m, i) => {
                                return (
                                    <Table.Row key={i}>
                                        <Table.Cell>{m.confidence.toFixed(2)}%</Table.Cell>
                                        <Table.Cell>{m.target}</Table.Cell>
                                        <Table.Cell>{m.type}</Table.Cell>
                                        {/* <Table.Cell>{m.status}</Table.Cell> */}
                                    </Table.Row>
                                );
                            })}
                        </Table>
                    </Segment>
                    <Header as="h4">Alternatives</Header>
                    {mapping.alternatives
                        .filter((a) => a.target !== 'SELF')
                        .map((a, i) => {
                            return (
                                <Segment key={i}>
                                    Confidence:{a.confidence.toFixed(2)}%<br />
                                    {a.descriptorId}
                                    <br />
                                    <Table className="descriptorMatches">
                                        {a.matchChecks.map((m, i) => {
                                            return (
                                                <Table.Row key={i}>
                                                    <Table.Cell>{m.confidence.toFixed(2)}%</Table.Cell>
                                                    <Table.Cell>{m.target}</Table.Cell>
                                                    <Table.Cell>{m.type}</Table.Cell>
                                                    {/* <Table.Cell>{m.status}</Table.Cell> */}
                                                </Table.Row>
                                            );
                                        })}
                                    </Table>
                                </Segment>
                            );
                        })}
                </div>
            }
            trigger={
                <div className="import-descriptor__content full-height">
                    <GripVertical className="grip-icon" />
                    <span className="flex-item-grow">{importDescriptor.label}</span>
                    {hasAlternativeMappings && (
                        <MappingsAlternativesIndication
                            highlightAlternatives={highlightAlternatives}
                            unhighlightAlternatives={() => {
                                setHighlightedMappingsMap({});
                            }}
                        />
                    )}
                    {newDescriptor && (
                        <Icon className="flex flex-align-center flex-justify-center" color="yellow" name="plus" />
                    )}
                    {isConfirmedByValueMatch(mapping) && <Icon color="green" name="check" className="cursorPointer grip-icon" />}
                    <Icon
                        onClick={(e: any) => {
                            e.stopPropagation();
                            handleRemoveImportDescriptor(mapping.rowId);
                        }}
                        name="trash"
                        className="cursorPointer grip-icon"
                    />
                </div>
            }
        />
    );
}


function isConfirmedByValueMatch(mapping: RowMappingType) {
    return (
        mapping.matchChecks.some(
            (c) => c.target === 'CORE_VALUE' && c.type === 'NUM_SIMILARITY' && c.confidence > 80
        ) &&
        mapping.matchChecks.some((c) => c.target !== 'CORE_VALUE' && c.confidence > 10) &&
        !mapping.alternatives
            .filter((a) => a.target !== 'SELF')
            .some((a) =>
                a.matchChecks.some((c) => c.target === 'CORE_VALUE' && c.type === 'NUM_SIMILARITY' && c.confidence > 50)
            )
    );
}

export default ImportDescriptor;
